import { FC, Fragment, useEffect, useRef, useState } from "react";

import { Box, Collapse, Stack, useTheme } from "@mui/material";
import {
  AvailablePaymentCurrency,
  formatCurrencyFromCents,
  getCurrencyFromCountry,
  getIsoLocaleFromCountry,
} from "@simplyk/common";
import { UseFormReturn } from "react-hook-form";

import { AmplitudeEvents } from "@/common/constants/amplitude";
import { isLengthLessThan } from "@/common/helpers/validators";
import { useAmplitude } from "@/common/hooks/amplitude/useAmplitude";
import { useMediaQuery } from "@/common/hooks/useMediaQuery";
import { useTranslate } from "@/common/hooks/useTranslate";
import { LiveEditorToggle } from "@/components/LiveEditorElements/LiveEditorToggle/LiveEditorToggle";
import { TabMenu } from "@/components/TabMenu/TabMenu";
import { DashboardTextField } from "@/dashboard-design-system/TextField/DashboardTextField";
import { DashboardTypography } from "@/dashboard-design-system/Typography/DashboardTypography";
import { useLiveFormEditorContext } from "@/features/LiveFormEditor/common/LiveFormEditorContext";
import { DONATION_FREQUENCIES } from "@/features/LiveFormEditor/Steps/StepSuggestedDonationAmounts/helpers/donationFrequencies";
import {
  Amount,
  DefaultFrequencyValue,
  StepSuggestedDonationAmountsFormData,
} from "@/features/LiveFormEditor/Steps/StepSuggestedDonationAmounts/types";
import { DashboardTranslationKey } from "@/public/@types/translations";

const MAX_MESSAGE_LENGTH = 500;

type FrequencyTab = DefaultFrequencyValue;

const FREQUENCY_CONFIG = DONATION_FREQUENCIES.map((frequency) => ({
  key: frequency.interval as FrequencyTab,
  name: frequency.amountsName,
  switchName: frequency.switchName,
  labelKey: frequency.labelKey,
}));

interface SpecifyImpactForAmountSelectedProps {
  form: UseFormReturn<StepSuggestedDonationAmountsFormData>;
  placeholders: DashboardTranslationKey[];
}

export const SpecifyImpactForAmountSelected: FC<SpecifyImpactForAmountSelectedProps> = ({ form, placeholders }) => {
  const { t } = useTranslate();
  const theme = useTheme();
  const { isPhoneScreen } = useMediaQuery();
  const { logAmplitudeEvent } = useAmplitude();
  const { apiFormObject, setPreviewAmountFocus } = useLiveFormEditorContext<"donation-form">();
  const [selectedTab, setSelectedTab] = useState<FrequencyTab>("oneTime");

  const enableAmountLabels = form.watch("enableAmountLabels");
  const frequencySwitches = form.watch(FREQUENCY_CONFIG.map((config) => config.switchName));

  const formCountry = apiFormObject.organization?.country;
  const locale = apiFormObject.locale;
  const currency = getCurrencyFromCountry(formCountry) as AvailablePaymentCurrency;
  const isoLocale = getIsoLocaleFromCountry(formCountry, locale);

  const formatAmount = (amountInCents: number | null) => {
    if (amountInCents === null) {
      return "";
    }
    return formatCurrencyFromCents(amountInCents, {
      isoLocale,
      currency,
      cents: "hiddenIfZero",
    });
  };

  const visibleTabs = FREQUENCY_CONFIG.filter((_, index) => frequencySwitches[index]);

  const effectiveTab: FrequencyTab = visibleTabs.some((t) => t.key === selectedTab)
    ? selectedTab
    : (visibleTabs[0]?.key ?? "oneTime");
  const { errors, submitCount } = form.formState;

  const lastHandledSubmitCount = useRef(0);
  useEffect(() => {
    if (submitCount === lastHandledSubmitCount.current) {
      return;
    }
    lastHandledSubmitCount.current = submitCount;
    const tabsWithError = visibleTabs.filter((config) => {
      const tabErrors = errors[config.name];
      return Array.isArray(tabErrors) && tabErrors.some(Boolean);
    });
    if (tabsWithError.length === 0 || tabsWithError.some((config) => config.key === effectiveTab)) {
      return;
    }
    setSelectedTab(tabsWithError[0].key);
  }, [submitCount, errors, visibleTabs, effectiveTab]);

  return (
    <Stack spacing={1.5}>
      <LiveEditorToggle
        label={t("dashboard", "liveFormEditor.stepSuggestedDonationAmounts.specifyImpactForAmountSelectedTitle")}
        control={form.control}
        name="enableAmountLabels"
        onValueChange={(checked) => {
          logAmplitudeEvent(
            checked ? AmplitudeEvents.SpecifyImpactAmountAdded : AmplitudeEvents.SpecifyImpactAmountRemoved
          );
        }}
      />

      <Collapse in={enableAmountLabels && visibleTabs.length > 0} timeout={300} easing="ease-in-out" unmountOnExit>
        <Stack spacing={2} sx={{ paddingTop: 1.5 }}>
          <Box
            sx={{
              width: "100%",
              minWidth: 0,
              ...(isPhoneScreen &&
                visibleTabs.length >= 4 && { overflowX: "auto", "& > *": { minWidth: "max-content" } }),
            }}
          >
            <TabMenu<FrequencyTab>
              currentTab={effectiveTab}
              onChange={(tab) => {
                setSelectedTab(tab);
                setPreviewAmountFocus({ recurrenceInterval: tab, amountIndex: 0 });
              }}
              tabs={visibleTabs.map((config) => ({ label: t("dashboard", config.labelKey), value: config.key }))}
              backgroundColor={theme.palette.surface.neutral.quiet}
              padding={0.5}
              fullWidthButtons
            />
          </Box>

          {visibleTabs.map((config) => {
            const isActiveTab = config.key === effectiveTab;
            const amounts = form.watch(config.name);
            return (
              <Box
                key={config.key}
                sx={{
                  display: isActiveTab ? "grid" : "none",
                  gridTemplateColumns: "auto 1fr",
                  columnGap: 2,
                  rowGap: 1.5,
                  alignItems: "center",
                }}
              >
                {amounts.map((amount: Amount, index: number) => {
                  const inputNamePrefix = `${config.name}.${index}` as const;
                  const messageError = errors[config.name]?.[index]?.message;
                  const messageErrorText = typeof messageError === "string" ? messageError : messageError?.message;
                  return (
                    <Fragment key={amount.id}>
                      <Box sx={{ paddingX: 2 }}>
                        <DashboardTypography variant="h6" vibe="text-neutral-moderate">
                          {formatAmount(amount.amount)}
                        </DashboardTypography>
                      </Box>
                      <Box sx={{ minWidth: 0 }}>
                        <input type="hidden" {...form.register(`${inputNamePrefix}.id`)} />
                        <DashboardTextField
                          name={`${inputNamePrefix}.message`}
                          control={form.control}
                          placeholder={placeholders[index] ? t("dashboard", placeholders[index]) : undefined}
                          defaultValue={amount.message}
                          data-test="amount-impact-message"
                          shouldUnregister={false}
                          debounced={300}
                          onFocus={() => {
                            setPreviewAmountFocus({ recurrenceInterval: config.key, amountIndex: index });
                          }}
                          rules={[
                            isLengthLessThan(
                              MAX_MESSAGE_LENGTH + 1,
                              t("dashboard", "messageTooLong", { max: MAX_MESSAGE_LENGTH })
                            ),
                          ]}
                          errorMessage={messageErrorText}
                        />
                      </Box>
                    </Fragment>
                  );
                })}
              </Box>
            );
          })}
        </Stack>
      </Collapse>
    </Stack>
  );
};
